import { useMemo } from "react";
import DataTable, { type TableColumn } from "react-data-table-component";
import { EyeIcon } from "lucide-react";
import { dataTableCustomStyles } from "../../config/dataTableTheme";
import type { KbQuestionItem, KbQuestionSortBy, KbQuestionSortDir } from "../../types/kbQuestion";
import { createDataTablePagination } from "./DataTablePagination";
import { withScrollableDataTable } from "./withScrollableDataTable";

const rfpsTablePagination = createDataTablePagination({
  rowsPerPageOptions: [10, 20, 30],
});
const ScrollableDataTable = withScrollableDataTable(DataTable);

type RFPsTableProps = {
  readonly questions: readonly KbQuestionItem[];
  readonly totalRows: number;
  readonly isLoading: boolean;
  readonly page: number;
  readonly perPage: number;
  readonly sortBy: KbQuestionSortBy;
  readonly sortDir: KbQuestionSortDir;
  readonly onChangePage: (page: number) => void;
  readonly onChangeRowsPerPage: (perPage: number) => void;
  readonly onSortChange: (sortBy: KbQuestionSortBy, sortDir: KbQuestionSortDir) => void;
  readonly onActionClick: (row: KbQuestionItem) => void;
};

const statusConfig: Record<"active" | "inactive", { bg: string; text: string }> = {
  active: { bg: "bg-emerald-50 border-emerald-200", text: "text-emerald-600" },
  inactive: { bg: "bg-gray-100 border-gray-200", text: "text-gray-600" },
};

type SortableColumn = TableColumn<KbQuestionItem> & {
  readonly sortField?: KbQuestionSortBy;
};

const StatusBadge = ({ isActive }: { readonly isActive: boolean }) => {
  const status = isActive ? "active" : "inactive";
  const config = statusConfig[status];
  return (
    <span
      className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-medium ${config.bg} ${config.text}`}
    >
      {status}
    </span>
  );
};

const formatDate = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

export const RFPsTable = ({
  questions,
  totalRows,
  isLoading,
  page,
  perPage,
  sortBy,
  sortDir,
  onChangePage,
  onChangeRowsPerPage,
  onSortChange,
  onActionClick,
}: RFPsTableProps) => {
  const columns: SortableColumn[] = useMemo(
    () => [
      {
        id: "question_text",
        sortField: "question_text",
        name: "Question",
        selector: (row) => row.question_text,
        sortable: true,
        grow: 2,
        cell: (row) => (
          <span className="line-clamp-2 font-medium text-gray-900" title={row.question_text}>
            {row.question_text}
          </span>
        ),
      },
      {
        id: "section_name",
        sortField: "section_name",
        name: "Section",
        selector: (row) => row.section_name,
        sortable: true,
        width: "180px",
        cell: (row) => (
          <span className="truncate text-sm text-gray-700" title={row.section_name}>
            {row.section_name}
          </span>
        ),
      },
      {
        id: "is_active",
        name: "Status",
        selector: (row) => (row.is_active ? "active" : "inactive"),
        sortable: false,
        width: "140px",
        cell: (row) => <StatusBadge isActive={row.is_active} />,
      },
      // {
      //   id: "source",
      //   sortField: "source",
      //   name: "Source",
      //   selector: (row) => row.source,
      //   sortable: true,
      //   width: "110px",
      //   cell: (row) => <span className="truncate text-sm text-gray-700">{row.source}</span>,
      // },
      {
        id: "created_at",
        sortField: "created_at",
        name: "Created",
        selector: (row) => row.created_at,
        sortable: true,
        width: "120px",
        cell: (row) => <span className="text-sm text-gray-700">{formatDate(row.created_at)}</span>,
      },
      {
        id: "updated_at",
        sortField: "updated_at",
        name: "Updated",
        selector: (row) => row.updated_at,
        sortable: true,
        width: "120px",
        cell: (row) => <span className="text-sm text-gray-700">{formatDate(row.updated_at)}</span>,
      },
      {
        id: "total_versions",
        sortField: "total_versions",
        name: "Versions",
        selector: (row) => row.total_versions,
        sortable: true,
        width: "120px",
        cell: (row) => <span className="text-sm text-gray-700">{row.total_versions}</span>,
      },
      {
        id: "current_answer_preview",
        name: "Answer Preview",
        selector: (row) => row.current_answer_preview ?? "",
        sortable: false,
        grow: 2,
        cell: (row) => (
          <span className="line-clamp-2 text-sm text-gray-700" title={row.current_answer_preview}>
            {row.current_answer_preview || "—"}
          </span>
        ),
      },
      {
        name: "Actions",
        cell: (row) => <EyeIcon className="h-5 w-5" onClick={() => onActionClick(row)} />,
        width: "130px",
        right: true,
      },
    ],
    [onActionClick],
  );

  return (
    <div className="min-h-0 w-full flex-1 overflow-hidden">
      <ScrollableDataTable
        columns={columns}
        data={[...questions]}
        customStyles={dataTableCustomStyles}
        striped={false}
        progressPending={isLoading}
        pagination
        paginationServer
        paginationTotalRows={totalRows}
        paginationPerPage={perPage}
        paginationDefaultPage={page}
        paginationRowsPerPageOptions={[10, 20, 30]}
        paginationComponent={rfpsTablePagination}
        onChangePage={onChangePage}
        onChangeRowsPerPage={(nextPerPage) => onChangeRowsPerPage(nextPerPage)}
        sortServer
        defaultSortFieldId={sortBy}
        defaultSortAsc={sortDir === "asc"}
        onSort={(column, direction) => {
          const sortField = (column as SortableColumn).sortField;
          if (!sortField) return;
          onSortChange(sortField, direction as KbQuestionSortDir);
        }}
        keyField="id"
        noDataComponent={
          <div className="p-12 text-center">
            <p className="text-sm text-gray-500">No questions found matching your search.</p>
          </div>
        }
        highlightOnHover
        responsive
      />
    </div>
  );
};
